"use client";

import { c as _c } from "react/compiler-runtime";
import { useQuery, type QueryFunction, type QueryKey } from "@tanstack/react-query";
import Image from "next/image";
import { useState } from "react";
import type { IconResponse } from "@domainstack/types";
import { Skeleton } from "@domainstack/ui/skeleton";
import { cn } from "@domainstack/ui/utils";
import { simpleHash } from "@domainstack/utils/simple-hash";
type IconQueryOptions<TQueryKey extends QueryKey = QueryKey> = {
  queryKey: TQueryKey;
  queryFn?: QueryFunction<IconQueryResult, TQueryKey>;
};
type IconQueryResult = {
  success: true;
  data: IconResponse;
} | {
  success: false;
};
export type RemoteIconProps<TQueryKey extends QueryKey = QueryKey> = {
  /**
   * TanStack Query options from tRPC's queryOptions() method.
   * Expected to resolve to `{ success: true, data: { url: string | null } }` or `{ success: false }`.
   */
  queryOptions: IconQueryOptions<TQueryKey>;
  /**
   * A URL the caller already knows (`null` = known to have none); skips the query when defined.
   * `undefined` means unknown, so the query runs as usual.
   */
  initialUrl?: string | null;
  /** Identifier for fallback avatar (e.g., domain name, provider name) */
  fallbackIdentifier: string;
  /** Size in pixels (default: 16) */
  size?: number;
  /** Additional CSS classes */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Alt text for the image */
  alt?: string;
  /** Data attribute for testing/debugging */
  dataAttribute?: string;
};

// Deterministic color palette - vibrant but not overwhelming
// Using actual color values to avoid Tailwind purging issues
const PLACEHOLDER_COLORS = ["#3b82f6",
// blue
"#a855f7",
// purple
"#ec4899",
// pink
"#f43f5e",
// rose
"#f97316",
// orange
"#f59e0b",
// amber
"#10b981",
// emerald
"#14b8a6",
// teal
"#06b6d4",
// cyan
"#6366f1",
// indigo
"#8b5cf6",
// violet
"#d946ef" // fuchsia
] as const;
const baseClassName = cn("pointer-events-none size-4 rounded-xs select-none");
function IconSkeleton(t0) {
  const $ = _c(5);
  const {
    className,
    style
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(baseClassName, "bg-input", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== style || $[3] !== t1) {
    t2 = <Skeleton className={t1} style={style} />;
    $[2] = style;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
type FallbackIconProps = Omit<RemoteIconProps, "queryOptions" | "initialUrl">;
function FallbackIcon(t0) {
  const $ = _c(17);
  const {
    size: t1,
    className,
    style,
    fallbackIdentifier,
    alt,
    dataAttribute
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  let t2;
  if ($[0] !== fallbackIdentifier[0]) {
    t2 = fallbackIdentifier[0]?.toUpperCase() || "?";
    $[0] = fallbackIdentifier[0];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const letter = t2;
  const backgroundColor = PLACEHOLDER_COLORS[simpleHash(fallbackIdentifier) % PLACEHOLDER_COLORS.length];
  const fontSize = Math.max(9, Math.floor(size * 0.55));
  let t3;
  if ($[2] !== className) {
    t3 = cn(baseClassName, "flex items-center justify-center font-bold text-white", className);
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== backgroundColor || $[5] !== fontSize || $[6] !== style) {
    t4 = {
      ...style,
      fontSize,
      backgroundColor
    };
    $[4] = backgroundColor;
    $[5] = fontSize;
    $[6] = style;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = alt ?? `${fallbackIdentifier} icon`;
  let t6;
  if ($[8] !== dataAttribute || $[9] !== fallbackIdentifier) {
    t6 = dataAttribute ? {
      [dataAttribute]: fallbackIdentifier
    } : {};
    $[8] = dataAttribute;
    $[9] = fallbackIdentifier;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== letter || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
    t7 = <div className={t3} style={t4} role="img" aria-label={t5} {...t6}>{letter}</div>;
    $[11] = letter;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}

/**
 * Shared component for rendering remote icons (favicons, logos, etc.)
 * with loading states, error handling, and letter avatar fallback.
 */
export function RemoteIcon(t0) {
  const $ = _c(31);
  const {
    size: t1,
    className,
    style,
    queryOptions,
    initialUrl,
    fallbackIdentifier,
    alt,
    dataAttribute
  } = t0;
  const size = t1 === undefined ? 32 : t1;
  const [failedUrl, setFailedUrl] = useState(null);
  let t2;
  if ($[0] !== initialUrl) {
    t2 = initialUrl === undefined ? undefined : {
      success: true as const,
      data: {
        url: initialUrl
      }
    };
    $[0] = initialUrl;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== queryOptions.queryFn || $[3] !== queryOptions.queryKey || $[4] !== t2) {
    t3 = {
      queryKey: queryOptions.queryKey,
      queryFn: queryOptions.queryFn,
      initialData: t2,
      retry: false,
      retryOnMount: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false
    };
    $[2] = queryOptions.queryFn;
    $[3] = queryOptions.queryKey;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    data: result,
    isLoading,
    isError
  } = useQuery(t3);
  if (isLoading) {
    let t4;
    if ($[6] !== className || $[7] !== style) {
      t4 = <IconSkeleton className={className} style={style} />;
      $[6] = className;
      $[7] = style;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  const url = result?.success ? result.data.url : undefined;
  if (isError || !url || failedUrl === url) {
    let t4;
    if ($[9] !== alt || $[10] !== className || $[11] !== dataAttribute || $[12] !== fallbackIdentifier || $[13] !== size || $[14] !== style) {
      t4 = <FallbackIcon size={size} className={className} style={style} fallbackIdentifier={fallbackIdentifier} alt={alt} dataAttribute={dataAttribute} />;
      $[9] = alt;
      $[10] = className;
      $[11] = dataAttribute;
      $[12] = fallbackIdentifier;
      $[13] = size;
      $[14] = style;
      $[15] = t4;
    } else {
      t4 = $[15];
    }
    return t4;
  }
  let t4;
  if ($[16] !== className) {
    t4 = cn(baseClassName, className);
    $[16] = className;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const t5 = alt ?? `${fallbackIdentifier} icon`;
  let t6;
  if ($[18] !== url) {
    t6 = () => setFailedUrl(url);
    $[18] = url;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== dataAttribute || $[21] !== fallbackIdentifier) {
    t7 = dataAttribute ? {
      [dataAttribute]: fallbackIdentifier
    } : {};
    $[20] = dataAttribute;
    $[21] = fallbackIdentifier;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== size || $[24] !== style || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== url) {
    t8 = <Image key={url} src={url} width={size} height={size} className={t4} style={style} unoptimized={true} priority={false} draggable={false} alt={t5} onError={t6} {...t7} />;
    $[23] = size;
    $[24] = style;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = url;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}